/**
 * GL-DESIGN-V3-2026Q4 — Finding capture sheet.
 *
 * Fast-path editor for the field: severity segmented, big dictate button,
 * AI citation suggest, photos, and the Dossier economics fields
 * (penalty_max / fix_cost / owner / due_date). Centered 620px modal on
 * iPad + desktop, bottom sheet on iPhone. Heavy/rare fields (repeat-finding
 * link, linked doc, internal notes, video) stay in the full editor.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { toast } from 'sonner';
import {
  CameraIcon,
  CheckIcon,
  SparkleIcon,
  XIcon,
  ArrowsOutSimpleIcon,
  TrashIcon,
} from '@phosphor-icons/react';
import { api, formatApiError, mediaUrl } from '@/lib/api';
import { useOnline } from '@/lib/useOnline';
import { enqueue, flushQueue } from '@/lib/offline';
import VoiceDictateButton from '@/components/VoiceDictateButton';
import { V3, IconButton, InsetInput, InsetTextArea, ScoreDial, Eyebrow, Segmented } from '@/components/v3/kit';

const SEVERITY = [
  { value: 'P1', label: 'P1 Immediate', color: V3.p1 },
  { value: 'P2', label: 'P2 Serious', color: V3.p2 },
  { value: 'P3', label: 'P3 Other', color: V3.p3 },
];
const STATUS = [
  { value: 'open', label: 'Open' },
  { value: 'in_progress', label: 'In progress' },
  { value: 'closed', label: 'Closed' },
  { value: 'verified_closed', label: 'Verified' },
];

// Dossier-ready completeness — each field that the Site Dossier prints.
const COMPLETENESS_FIELDS = [
  ['violation_type', (f) => !!f.violation_type.trim()],
  ['description', (f) => !!f.description.trim()],
  ['corrective_action', (f) => !!f.corrective_action.trim()],
  ['cfr_citation', (f) => !!f.cfr_citation.trim()],
  ['location', (f) => !!f.location.trim()],
  ['photo', (f) => (f.photo_ids || []).length > 0],
  ['penalty_max', (f) => f.penalty_max !== '' && f.penalty_max !== null],
  ['owner', (f) => !!f.owner.trim()],
  ['due_date', (f) => !!f.due_date],
];

function toInt(v) {
  if (v === '' || v === null || v === undefined) return null;
  const n = Number(String(v).replace(/[^0-9.]/g, ''));
  return Number.isFinite(n) ? Math.round(n) : null;
}

function Label({ children, right }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
      <span style={{ fontSize: 13, fontWeight: 600, color: V3.navy }}>{children}</span>
      {right}
    </div>
  );
}

function SeveritySegmented({ value, onChange }) {
  return (
    <div
      data-testid="capture-severity"
      style={{ display: 'flex', gap: 4, background: V3.surface, borderRadius: 999, padding: 5, boxShadow: V3.insetSm }}
    >
      {SEVERITY.map((s) => {
        const active = s.value === value;
        return (
          <button
            key={s.value}
            type="button"
            onClick={() => onChange(s.value)}
            data-testid={`capture-severity-${s.value}`}
            style={{
              flex: 1,
              height: 40,
              borderRadius: 999,
              border: 0,
              cursor: 'pointer',
              fontSize: 13.5,
              fontWeight: 600,
              background: active ? s.color : 'transparent',
              color: active ? '#fff' : V3.muted,
              boxShadow: active ? '0 6px 12px -6px rgba(20,38,84,.5)' : 'none',
              transition: 'background-color 140ms ease, color 140ms ease',
              whiteSpace: 'nowrap',
            }}
          >
            {s.label}
          </button>
        );
      })}
    </div>
  );
}

export default function FindingCaptureModal({ eng, initial, onClose, onSaved, onOpenFull }) {
  const online = useOnline();
  const isPhone = typeof window !== 'undefined' && window.matchMedia('(max-width: 767px)').matches;
  const [form, setForm] = useState({
    priority: initial?.priority || 'P2',
    violation_type: initial?.violation_type || '',
    description: initial?.description || '',
    corrective_action: initial?.corrective_action || '',
    cfr_citation: initial?.cfr_citation || '',
    standard_name: initial?.standard_name || '',
    penalty_exposure: initial?.penalty_exposure || '',
    location: initial?.location || '',
    status: initial?.status || 'open',
    photo_ids: initial?.photo_ids || [],
    penalty_max: initial?.penalty_max ?? '',
    fix_cost_low: initial?.fix_cost_low ?? '',
    fix_cost_high: initial?.fix_cost_high ?? '',
    owner: initial?.owner || initial?.suggested_responsible_party || '',
    due_date: initial?.due_date || (initial?.corrective_due_date && /^\d{4}-\d{2}-\d{2}$/.test(initial.corrective_due_date) ? initial.corrective_due_date : ''),
  });
  const [photos, setPhotos] = useState([]);
  const [busy, setBusy] = useState(false);
  const [ai, setAi] = useState('idle'); // idle | loading | done
  const [aiResult, setAiResult] = useState(null);
  const [aiAccepted, setAiAccepted] = useState(false);
  const fileRef = useRef(null);

  const up = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    const ids = form.photo_ids || [];
    if (!ids.length) {
      setPhotos([]);
      return;
    }
    Promise.all(ids.map((id) => api.get(`/media/${id}`).then((r) => r.data).catch(() => null))).then((r) => setPhotos(r.filter(Boolean)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.photo_ids?.length]);

  useEffect(() => {
    const onKey = (e) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [onClose]);

  const completeness = useMemo(() => {
    const done = COMPLETENESS_FIELDS.filter(([, fn]) => fn(form)).length;
    return Math.round((done / COMPLETENESS_FIELDS.length) * 100);
  }, [form]);

  async function suggestCitation() {
    const seed = form.description.trim() || form.violation_type.trim();
    if (!seed) {
      toast.error('Describe the observation first — the AI needs something to cite.');
      return;
    }
    if (!online) {
      toast.error('Citation lookup needs connection.');
      return;
    }
    setAi('loading');
    setAiAccepted(false);
    try {
      const { data } = await api.post('/ai/cfr_lookup', { hazard_description: seed });
      setAiResult(data);
      setAi('done');
    } catch (e) {
      toast.error(formatApiError(e));
      setAi('idle');
    }
  }

  function acceptCitation() {
    if (!aiResult) return;
    setForm((f) => ({
      ...f,
      cfr_citation: aiResult.cfr_citation || f.cfr_citation,
      standard_name: aiResult.standard_name || f.standard_name,
      penalty_exposure: aiResult.penalty_exposure || f.penalty_exposure,
      corrective_action: f.corrective_action.trim() ? f.corrective_action : aiResult.corrective_action || '',
      violation_type: f.violation_type.trim() ? f.violation_type : aiResult.standard_name || '',
    }));
    setAiAccepted(true);
  }

  async function onUpload(e) {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    if (!online) {
      toast.error('Photo upload needs connection.');
      return;
    }
    for (const file of files) {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('caption', '');
      try {
        const { data } = await api.post(`/engagements/${eng.id}/media`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        setForm((f) => ({ ...f, photo_ids: [...(f.photo_ids || []), data.id] }));
      } catch (err) {
        toast.error(formatApiError(err));
      }
    }
    e.target.value = '';
  }

  async function save() {
    if (!form.violation_type.trim() && !form.description.trim()) {
      toast.error('Add a title or an observation first');
      return;
    }
    setBusy(true);
    try {
      const payload = {
        ...form,
        penalty_max: toInt(form.penalty_max),
        fix_cost_low: toInt(form.fix_cost_low),
        fix_cost_high: toInt(form.fix_cost_high),
        is_resolved: form.status === 'closed' || form.status === 'verified_closed',
        suggested_responsible_party: form.owner,
        corrective_due_date: form.due_date || initial?.corrective_due_date || '',
      };
      let saved = null;
      if (initial) {
        if (!online) {
          await enqueue({ type: 'update_finding', finding_id: initial.id, payload });
          toast('Saved offline · will sync');
          saved = { ...initial, ...payload };
        } else {
          saved = (await api.patch(`/findings/${initial.id}`, payload)).data;
        }
      } else if (!online) {
        await enqueue({ type: 'create_finding', engagement_id: eng.id, payload });
        toast('Saved offline · will sync');
      } else {
        saved = (await api.post(`/engagements/${eng.id}/findings`, payload)).data;
      }
      flushQueue();
      toast.success(initial ? `Finding ${String(initial.number).padStart(2, '0')} saved` : 'Finding logged');
      onSaved(saved);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  }

  const title = initial ? `Finding ${String(initial.number).padStart(2, '0')}` : 'New finding';

  const sheet = (
    <div
      data-testid="finding-capture-backdrop"
      onClick={(e) => e.target === e.currentTarget && onClose()}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 80,
        background: 'rgba(10,21,53,.45)',
        backdropFilter: 'blur(6px)',
        display: 'flex',
        alignItems: isPhone ? 'flex-end' : 'center',
        justifyContent: 'center',
        padding: isPhone ? 0 : 20,
      }}
    >
      <div
        data-testid="finding-capture-modal"
        role="dialog"
        aria-label={title}
        style={{
          width: isPhone ? '100%' : 620,
          maxHeight: isPhone ? '94vh' : 'calc(100vh - 40px)',
          overflowY: 'auto',
          background: V3.surface,
          borderRadius: isPhone ? '32px 32px 0 0' : 32,
          boxShadow: V3.raisedLg,
          padding: isPhone ? '18px 18px 28px' : 24,
          display: 'flex',
          flexDirection: 'column',
          gap: 20,
        }}
      >
        {/* header */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          <IconButton icon="x" size={44} onClick={onClose} testid="capture-close" title="Close" />
          <div style={{ flex: 1, minWidth: 0 }}>
            <Eyebrow>{eng.business_name}</Eyebrow>
            <div data-testid="capture-title" style={{ fontSize: 22, fontWeight: 700, color: V3.navy, letterSpacing: '-0.02em' }}>{title}</div>
          </div>
          <ScoreDial pct={completeness} size={84} label="ready" testid="capture-completeness" />
          <button
            type="button"
            onClick={save}
            disabled={busy}
            data-testid="capture-save"
            style={{
              height: 48,
              padding: '0 22px',
              borderRadius: 999,
              border: 0,
              background: V3.navy,
              color: '#fff',
              fontWeight: 600,
              fontSize: 15,
              cursor: busy ? 'wait' : 'pointer',
              boxShadow: V3.navyBtn,
              opacity: busy ? 0.7 : 1,
            }}
          >
            {busy ? 'Saving…' : 'Save'}
          </button>
        </div>

        {/* severity */}
        <div>
          <Label>Severity</Label>
          <SeveritySegmented value={form.priority} onChange={(v) => up('priority', v)} />
        </div>

        {/* title + location */}
        <div style={{ display: 'grid', gridTemplateColumns: isPhone ? '1fr' : '1.4fr 1fr', gap: 14 }}>
          <div>
            <Label>Title</Label>
            <InsetInput value={form.violation_type} onChange={(v) => up('violation_type', v)} placeholder="Short hazard label" testid="capture-title-input" />
          </div>
          <div>
            <Label>Location</Label>
            <InsetInput value={form.location} onChange={(v) => up('location', v)} placeholder="Receiving, Bay 2" testid="capture-location" />
          </div>
        </div>

        {/* observation + dictate */}
        <div>
          <Label>Observation</Label>
          <div style={{ display: 'flex', gap: 16, alignItems: 'stretch' }}>
            <div style={{ flex: 1 }}>
              <InsetTextArea
                value={form.description}
                onChange={(v) => up('description', v)}
                placeholder="What you saw, where, and who was exposed."
                testid="capture-description"
                minHeight={136}
              />
            </div>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <VoiceDictateButton
                variant="hero"
                testid="capture-dictate"
                onTranscript={(t) => up('description', (form.description ? `${form.description} ${t}` : t).trim())}
              />
            </div>
          </div>
        </div>

        {/* citation */}
        <div>
          <Label
            right={
              <button
                type="button"
                onClick={suggestCitation}
                disabled={ai === 'loading'}
                data-testid="capture-suggest-ai"
                style={{
                  height: 34,
                  padding: '0 14px',
                  borderRadius: 999,
                  border: 0,
                  background: V3.tint,
                  color: V3.navy,
                  fontSize: 12.5,
                  fontWeight: 600,
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 6,
                  cursor: ai === 'loading' ? 'wait' : 'pointer',
                }}
              >
                <SparkleIcon size={14} weight="fill" /> {ai === 'loading' ? 'Thinking…' : online ? 'Suggest with AI' : 'Needs connection'}
              </button>
            }
          >
            OSHA citation
          </Label>
          <InsetInput value={form.cfr_citation} onChange={(v) => up('cfr_citation', v)} placeholder="29 CFR 1910.___" testid="capture-cfr" />
          {ai === 'done' && aiResult && (
            <div data-testid="capture-ai-card" style={{ marginTop: 12, background: V3.tint, borderRadius: 22, padding: 16, display: 'flex', flexDirection: 'column', gap: 8 }}>
              <div style={{ fontFamily: V3.mono, fontSize: 12.5, color: 'var(--v3-mid-navy)', fontWeight: 700 }}>{aiResult.cfr_citation}</div>
              <div style={{ fontSize: 15, fontWeight: 600, color: V3.ink }}>{aiResult.standard_name}</div>
              {aiResult.penalty_exposure && <div style={{ fontSize: 13, color: V3.muted }}><b style={{ color: V3.ink }}>Penalty:</b> {aiResult.penalty_exposure}</div>}
              {aiResult.corrective_action && <div style={{ fontSize: 13, color: V3.muted }}><b style={{ color: V3.ink }}>Fix:</b> {aiResult.corrective_action}</div>}
              <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
                <button
                  type="button"
                  onClick={acceptCitation}
                  data-testid="capture-ai-accept"
                  style={{
                    height: 40,
                    padding: '0 18px',
                    borderRadius: 999,
                    border: 0,
                    background: aiAccepted ? V3.p3 : V3.navy,
                    color: '#fff',
                    fontWeight: 600,
                    fontSize: 13.5,
                    cursor: 'pointer',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 6,
                  }}
                >
                  {aiAccepted && <CheckIcon size={14} weight="bold" />} {aiAccepted ? 'Accepted' : 'Accept'}
                </button>
                <button
                  type="button"
                  onClick={() => setAi('idle')}
                  data-testid="capture-ai-dismiss"
                  style={{ height: 40, padding: '0 16px', borderRadius: 999, background: 'transparent', border: `1.5px solid ${V3.navy}`, color: V3.navy, fontWeight: 600, fontSize: 13.5, cursor: 'pointer' }}
                >
                  Edit
                </button>
              </div>
            </div>
          )}
        </div>

        {/* corrective action */}
        <div>
          <Label>Corrective action</Label>
          <div style={{ display: 'flex', gap: 16, alignItems: 'center' }}>
            <div style={{ flex: 1 }}>
              <InsetTextArea
                value={form.corrective_action}
                onChange={(v) => up('corrective_action', v)}
                placeholder="What has to change, concretely."
                testid="capture-corrective"
                minHeight={96}
              />
            </div>
            <VoiceDictateButton
              variant="hero"
              testid="capture-dictate-corrective"
              onTranscript={(t) => up('corrective_action', (form.corrective_action ? `${form.corrective_action} ${t}` : t).trim())}
            />
          </div>
        </div>

        {/* economics + roadmap */}
        <div>
          <Label right={<span style={{ fontSize: 12, color: V3.muted }}>Feeds the Dossier exposure math</span>}>Penalty & cost</Label>
          <div style={{ display: 'grid', gridTemplateColumns: isPhone ? '1fr 1fr' : '1fr 1fr 1fr', gap: 12 }}>
            <InsetInput type="number" value={form.penalty_max} onChange={(v) => up('penalty_max', v)} placeholder="Max penalty $ (16,550)" testid="capture-penalty-max" />
            <InsetInput type="number" value={form.fix_cost_low} onChange={(v) => up('fix_cost_low', v)} placeholder="Fix cost low $" testid="capture-fix-low" />
            <InsetInput type="number" value={form.fix_cost_high} onChange={(v) => up('fix_cost_high', v)} placeholder="Fix cost high $" testid="capture-fix-high" />
          </div>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: isPhone ? '1fr' : '1fr 1fr', gap: 14 }}>
          <div>
            <Label>Owner</Label>
            <InsetInput value={form.owner} onChange={(v) => up('owner', v)} placeholder="Ops supervisor" testid="capture-owner" />
          </div>
          <div>
            <Label>Correct by</Label>
            <InsetInput type="date" value={form.due_date} onChange={(v) => up('due_date', v)} testid="capture-due-date" />
            <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
              {[['7d', 7], ['30d', 30], ['90d', 90]].map(([lab, days]) => (
                <button
                  key={lab}
                  type="button"
                  data-testid={`capture-due-quick-${lab}`}
                  onClick={() => {
                    const d = new Date();
                    d.setDate(d.getDate() + days);
                    up('due_date', d.toISOString().slice(0, 10));
                  }}
                  style={{ height: 30, padding: '0 12px', borderRadius: 999, border: 0, background: V3.tint, color: V3.navy, fontFamily: V3.mono, fontSize: 11.5, fontWeight: 700, cursor: 'pointer' }}
                >
                  +{lab}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* status */}
        <div>
          <Label>Status</Label>
          <Segmented options={STATUS} value={form.status} onChange={(v) => up('status', v)} testid="capture-status" />
        </div>

        {/* photos */}
        <div>
          <Label right={<span style={{ fontSize: 12, color: V3.muted }}>{photos.length} photo{photos.length === 1 ? '' : 's'}</span>}>Photos</Label>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
            <button
              type="button"
              onClick={() => fileRef.current?.click()}
              data-testid="capture-photo-tile"
              style={{
                width: 72,
                height: 72,
                borderRadius: 22,
                border: 0,
                background: V3.surface,
                boxShadow: V3.raisedSm,
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
              }}
            >
              <span style={{ width: 44, height: 44, borderRadius: '50%', background: V3.navy, color: '#fff', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
                <CameraIcon size={22} weight="fill" />
              </span>
            </button>
            <input ref={fileRef} type="file" accept="image/*" capture="environment" multiple onChange={onUpload} style={{ display: 'none' }} data-testid="capture-photo-input" />
            {photos.map((p) => (
              <div key={p.id} style={{ position: 'relative', width: 72, height: 72, borderRadius: 22, overflow: 'hidden', boxShadow: V3.insetSm, background: V3.tint }}>
                <img src={mediaUrl(p.id)} alt={p.caption || 'finding'} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                <button
                  type="button"
                  aria-label="Remove photo"
                  data-testid={`capture-photo-remove-${p.id}`}
                  onClick={() => up('photo_ids', form.photo_ids.filter((x) => x !== p.id))}
                  style={{ position: 'absolute', top: 4, right: 4, width: 22, height: 22, borderRadius: '50%', border: 0, background: 'rgba(20,38,84,.85)', color: '#fff', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}
                >
                  <TrashIcon size={12} weight="bold" />
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* footer */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap' }}>
          {onOpenFull ? (
            <button
              type="button"
              onClick={onOpenFull}
              data-testid="capture-open-full"
              style={{ height: 44, padding: '0 16px', borderRadius: 999, border: `1.5px solid ${V3.navy}`, background: 'transparent', color: V3.navy, fontWeight: 600, fontSize: 13.5, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 8 }}
            >
              <ArrowsOutSimpleIcon size={16} weight="bold" /> Full editor
            </button>
          ) : <span />}
          <button
            type="button"
            onClick={onClose}
            data-testid="capture-cancel"
            style={{ height: 44, padding: '0 16px', borderRadius: 999, border: 0, background: 'transparent', color: V3.muted, fontWeight: 600, fontSize: 13.5, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6 }}
          >
            <XIcon size={14} weight="bold" /> Cancel
          </button>
        </div>
      </div>
    </div>
  );

  return createPortal(sheet, document.body);
}
